/* eslint-disable */
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import { toast } from 'App';
import clsx from 'clsx';
import { Button } from 'components/button';
import Card from 'components/card';
import { useState } from 'react';
import useSWR from 'swr';
import { formatDate } from 'utils/date';
import { formatUnits } from 'viem';
import TransactionDetails from 'views/admin/Transactions/components/TransactionDetails/TransactionDetails';
import { txsService } from 'views/services/txsService';
import { usersService } from 'views/services/usersService';
import { useUsersStore } from 'views/store/useUsersStore';
import { ReferralReward } from 'views/types/referrals';
import { IDepositExtended } from 'views/types/typeTransaction';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { User } from 'views/types/typeUser';

interface UserReferralsProps {
  user: User;
  onUserClick?: (userId: string) => void;
}

const columnHelper = createColumnHelper<ReferralReward>();

export const UserReferrals: React.FC<UserReferralsProps> = ({
  user,
  onUserClick,
}) => {
  const [tier, setTier] = useState<'firstTier' | 'secondTier'>('firstTier');
  const { canRead } = usePermissions();
  const [selectedReferralTransactions, setSelectedReferralTransactions] =
    useState<null | IDepositExtended[]>(null);
  const [selectedTransaction, setSelectedTransaction] =
    useState<null | IDepositExtended>(null);
  const [loading, setLoading] = useState(false);
  const { data, isLoading } = useSWR(
    user.id ? `user-referrals-${user.id}` : null,
    () => usersService.getUserReferrals(user.id),
    { refreshInterval: 10000 }
  );

  const { setUserOfReferral } = useUsersStore();

  const columns = [
    columnHelper.accessor('coinsflyId', {
      header: 'Client ID',
      cell: info => (
        <button
          onClick={() => {
            setUserOfReferral(user);
            onUserClick?.(info.getValue());
          }}
          className="text-brand-500 hover:text-brand-600 hover:underline font-mono text-sm cursor-pointer font-medium"
        >
          {info.getValue()}
        </button>
      ),
    }),
    columnHelper.accessor('username', {
      header: 'Telegram username',
      cell: info => (
        <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
          {info.getValue() || '-'}
        </span>
      ),
    }),
    columnHelper.accessor('referralRewards', {
      header: 'Referral Rewards',
      cell: info => {
        const transactions = info.row.original.transactions;
        const onTransactionsClick = () => {
          setSelectedReferralTransactions(transactions);
        };

        return (
          <span
            className="font-semibold text-green-600 dark:text-green-400 cursor-pointer"
            onClick={onTransactionsClick}
          >
            {parseFloat(info.getValue())} USDT
          </span>
        );
      },
    }),
  ];

  const fetchSourceTxData = async (id?: string) => {
    if (!id) {
      toast({
        title: 'Warning',
        description: 'There are no source transaction id!',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await txsService.getAllTransactions({ search: id });
      const tx = data?.[0];
      setSelectedTransaction(tx);
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: 'Error receiving source transaction data',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const table = useReactTable({
    data: data?.referralsRewards?.[tier] ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {!!selectedReferralTransactions ? (
        <>
          {loading ? (
            <span>Loading...</span>
          ) : (
            <>
              {selectedTransaction ? (
                <Card
                  extra={
                    'w-full h-[90%] md:h-[90%] lg-max:max-h-[90%] overflow-y-auto sm:overflow-auto px-6 pb-6'
                  }
                >
                  <TransactionDetails
                    transaction={selectedTransaction}
                    onBack={() => setSelectedTransaction(null)}
                  />
                </Card>
              ) : (
                <>
                  <Button
                    onClick={() => setSelectedReferralTransactions(null)}
                    className="justify-center bg-blue-600 hover:bg-blue-700 "
                  >
                    Back
                  </Button>
                  {!!selectedReferralTransactions.length ? (
                    <>
                      <div className="grid grid-cols-5 font-bold gap-4 px-4 py-2 m-1 mb-4">
                        <p>Source TX ID</p>
                        <p>Percentage</p>
                        <p>Amount</p>
                        <p className="capitalize">TX type</p>
                        <p>Date</p>
                      </div>
                      {selectedReferralTransactions.map(tx => {
                        const amountRaw = tx.amountIn;
                        const currency = tx.currencyIn;
                        const decimals = currency?.decimals || 0;
                        const formattedAmount = +formatUnits(
                          BigInt(Math.abs(Math.trunc(+amountRaw))),
                          decimals
                        );
                        const percentageString =
                          (+tx.referralPercentage * 100).toString() + '%';

                        return (
                          <div
                            key={tx.id}
                            onClick={() => {
                              if (
                                canRead(
                                  PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE
                                ) &&
                                tx.sourceTransactionId
                              ) {
                                fetchSourceTxData(tx.sourceTransactionId);
                              }
                            }}
                            className={`grid grid-cols-5 gap-4 px-4 py-2 border m-1 ${
                              canRead(
                                PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE
                              ) && tx.sourceTransactionId
                                ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700'
                                : 'cursor-not-allowed opacity-60'
                            }`}
                          >
                            <p
                              title={tx.sourceTransactionId || ''}
                              className="font-medium font-mono text-sm"
                            >
                              {tx.sourceTransactionId ? (
                                <>
                                  <span className="inline">
                                    {`${tx.sourceTransactionId.slice(0, 8)}...${tx.sourceTransactionId.slice(-6)}`}
                                  </span>
                                </>
                              ) : (
                                '-'
                              )}
                            </p>

                            <p>{percentageString}</p>
                            <p>{`${formattedAmount.toFixed(6)} ${currency.symbol}`}</p>
                            <p className="capitalize">
                              {tx.txType.toLowerCase()}
                            </p>
                            <p>
                              {formatDate(tx.createdAt, 'DD.MM.YYYY HH:mm')}
                            </p>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <p className="text-[20px] text-center">
                      There are no referral transaction yet
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white dark:bg-navy-800 p-4 border border-gray-300 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Referral Count
              </h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {data?.referralCount ?? 0}
              </p>
            </div>
            <div className="bg-white dark:bg-navy-800 p-4 border border-gray-300 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Rewarded Referrals
              </h3>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {data?.rewardedReferralsCount ?? 0}
              </p>
            </div>
          </div>
          <div className="flex gap-4 mb-4">
            <Button
              onClick={() => setTier('firstTier')}
              className={clsx(
                'justify-center',
                tier === 'firstTier'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-600 hover:bg-gray-700'
              )}
            >
              1 tier
            </Button>
            <Button
              onClick={() => setTier('secondTier')}
              className={clsx(
                'justify-center',
                tier === 'secondTier'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-600 hover:bg-gray-700'
              )}
            >
              2 tier
            </Button>
          </div>

          {isLoading ? (
            <span>Loading...</span>
          ) : data?.referralsRewards?.[tier].length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          className="bg-white pb-2 px-4 pt-4 text-start dark:bg-navy-800"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => (
                    <tr
                      key={row.id}
                      className="[&_td]:odd:bg-gray-50 [&_td]:odd:dark:bg-navy-900"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="p-2 px-4">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No referral rewards data available
            </div>
          )}
        </>
      )}
    </>
  );
};

export default UserReferrals;
